import React, { createRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { $getNodeByKey } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';

import { DropdownMenuItem } from '../DropdownMenuItem';
import { Tooltip } from '../Tooltip';

import { Avatar, Text, Icon } from '../../atoms';
import { Dropdown } from '../../organisms/Dropdown';

const WrapText = styled.p`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 260px;
`;

const MentionContainer = styled.span`
  display: inline-flex;
  box-sizing: border-box;
  align-items: flex-start;
  background: var(--gray-8);
  border-radius: 4px;
  column-gap: 2px;
  padding: 2px;
  min-height: 20px;
  line-height: 20px;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;

  ${({ $isSelected }) =>
    $isSelected &&
    `
    box-shadow:  0 0 0 2px var(--white), 0 0 0 3px var(--primary-hover);
  `}
`;

const SpanTextBreakSpaces = styled(Text)`
  white-space: break-spaces;
  flex-shrink: 0;
`;

const SpanTextNoWrap = styled(Text)`
  white-space: nowrap;
`;

const EnumToggleButton = styled.button.attrs({ type: 'button' })`
  white-space: break-spaces;
  flex-shrink: 0;

  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  color: var(--content-subtitle);
  border-radius: 2px;

  transition: color 0.1s ease-in-out, background 0.1s ease-in-out;

  &:hover {
    background: var(--wash-gray-hover);
    color: var(--action-ternary-hover);
  }

  &:active {
    background: var(--wash-gray-pressed);
    color: var(--action-ternary-hover);
  }
`;

const StyledTooltip = styled(Tooltip)`
  display: inline-flex;
  column-gap: 2px;
  flex-wrap: wrap;
  max-width: 100%;
`;

const ColumnMentionComponent = forwardRef(({ nodeKey, selectedEnumValue = null, data }, ref) => {
  const [editor] = useLexicalComposerContext();
  const columnTooltipRef = createRef(null);
  const enumValuesTooltipRef = createRef(null);
  const handleInnerTooltipMouseEnter = (e) => {
    columnTooltipRef.current.close();
    e.stopPropagation();
  };

  const handleSelectEnum = (enumValue) => {
    // Wrap the state update in an editor.update() call
    editor.update(() => {
      const node = $getNodeByKey(nodeKey);
      if (node) {
        node.getWritable().selectedEnumValue = enumValue;
      }
    });
  };

  const [isSelected] = useLexicalNodeSelection(nodeKey);

  return (
    <MentionContainer ref={ref} $isSelected={isSelected}>
      <StyledTooltip
        ref={columnTooltipRef}
        content={data.columnDescription ? <WrapText>{data.columnDescription}</WrapText> : null}
        position='top'
      >
        <>
          <Avatar width={20} src={data.source?.integration?.pictureUrl} />
          <SpanTextBreakSpaces as='span'>{data.column}</SpanTextBreakSpaces>
          <SpanTextNoWrap as='span' $color='var(--content-subtitle)'>
            ({data.view})
          </SpanTextNoWrap>
          {data.enumeratedValues?.length > 0 && (
            <Dropdown
              onMouseEnter={handleInnerTooltipMouseEnter}
              menuPlacement='bottom-end'
              menuStyle={{
                width: 180
              }}
              toggleComponent={
                selectedEnumValue ? (
                  <EnumToggleButton as='span'>= {selectedEnumValue}</EnumToggleButton>
                ) : (
                  <EnumToggleButton onClick={() => enumValuesTooltipRef.current.close()}>
                    <Tooltip
                      ref={enumValuesTooltipRef}
                      content='Show all possible values'
                      onMouseEnter={handleInnerTooltipMouseEnter}
                    >
                      <Icon name='plus' displaySize={16} />
                    </Tooltip>
                  </EnumToggleButton>
                )
              }
            >
              {() => (
                <>
                  {data.enumeratedValues.map((enumValue) => (
                    <DropdownMenuItem onClick={() => handleSelectEnum(enumValue)} key={enumValue}>
                      {enumValue}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </Dropdown>
          )}
        </>
      </StyledTooltip>
    </MentionContainer>
  );
});

ColumnMentionComponent.propTypes = {
  nodeKey: PropTypes.string.isRequired,
  selectedEnumValue: PropTypes.string,
  data: PropTypes.shape({
    columnDescription: PropTypes.string,
    source: PropTypes.shape({
      integration: PropTypes.shape({
        pictureUrl: PropTypes.string
      })
    }),
    column: PropTypes.string.isRequired,
    view: PropTypes.string.isRequired,
    enumeratedValues: PropTypes.arrayOf(PropTypes.string)
  }).isRequired
};

export { ColumnMentionComponent };
